// 🎯 등급별 가격 관리 유틸리티 함수
// 최고 수준의 코드 품질과 성능을 위한 최적화된 함수들

import { 
  DeviceGrade, 
  GradePriceInfo, 
  PriceValidationResult, 
  GRADE_CONSTANTS,
  PRICE_VALIDATION_RULES 
} from '../types/gradeSystem';

/**
 * 가격 단조성 검증 (A ≥ B ≥ C ≥ D)
 * @param gradePrices 등급별 가격 정보
 * @returns 검증 결과
 */
export const validateMonotonicity = (gradePrices: GradePriceInfo): PriceValidationResult => {
  const grades: DeviceGrade[] = ['A', 'B', 'C', 'D', 'E'];
  const errors: string[] = [];
  
  for (let i = 0; i < grades.length - 1; i++) {
    const currentGrade = grades[i];
    const nextGrade = grades[i + 1];
    const currentPrice = gradePrices[currentGrade];
    const nextPrice = gradePrices[nextGrade];
    
    // null 값은 검증에서 제외
    if (currentPrice !== null && nextPrice !== null && currentPrice < nextPrice) {
      errors.push(
        `${currentGrade}등급 가격(${currentPrice.toLocaleString()}원)이 ${nextGrade}등급 가격(${nextPrice.toLocaleString()}원)보다 낮을 수 없습니다.`
      );
    }
  }
  
  return {
    isValid: errors.length === 0,
    error: errors.length > 0 ? errors[0] : undefined,
    warnings: errors.length > 1 ? errors.slice(1) : undefined,
  };
};

/**
 * 가격 범위 검증
 * @param price 검증할 가격
 * @returns 검증 결과
 */
export const validatePriceRange = (price: number | null): PriceValidationResult => {
  if (price === null) {
    return { isValid: true };
  }
  
  if (price < PRICE_VALIDATION_RULES.MIN_PRICE) {
    return {
      isValid: false,
      error: `최소 가격은 ${PRICE_VALIDATION_RULES.MIN_PRICE.toLocaleString()}원입니다.`,
    };
  }
  
  if (price > PRICE_VALIDATION_RULES.MAX_PRICE) {
    return {
      isValid: false,
      error: `최대 가격은 ${PRICE_VALIDATION_RULES.MAX_PRICE.toLocaleString()}원입니다.`,
    };
  }
  
  return { isValid: true };
};

/**
 * 전체 가격 정보 검증
 * @param gradePrices 등급별 가격 정보
 * @returns 검증 결과
 */
export const validateGradePrices = (gradePrices: GradePriceInfo): PriceValidationResult => {
  // 개별 가격 범위 검증
  const rangeValidations = Object.entries(gradePrices).map(([grade, price]) => 
    validatePriceRange(price)
  );
  
  const rangeErrors = rangeValidations
    .filter(v => !v.isValid)
    .map(v => v.error!)
    .filter(Boolean);
  
  if (rangeErrors.length > 0) {
    return {
      isValid: false,
      error: rangeErrors[0],
      warnings: rangeErrors.slice(1),
    };
  }
  
  // 단조성 검증
  if (PRICE_VALIDATION_RULES.MONOTONICITY) {
    return validateMonotonicity(gradePrices);
  }
  
  return { isValid: true };
};

/**
 * 가격 포맷팅 (₩1,000,000 형식)
 * @param price 가격
 * @returns 포맷된 가격 문자열
 */
export const formatPrice = (price: number | null): string => {
  if (price === null) return '미설정';
  return `₩${price.toLocaleString()}`;
};

/**
 * 가격 파싱 (문자열을 숫자로 변환)
 * @param priceString 가격 문자열
 * @returns 파싱된 가격 (null if invalid)
 */
export const parsePrice = (priceString: string): number | null => {
  if (!priceString || priceString.trim() === '') return null;
  
  // 숫자만 추출 (콤마, 공백 제거)
  const cleanString = priceString.replace(/[^\d]/g, '');
  const parsed = parseInt(cleanString, 10);
  
  return isNaN(parsed) ? null : parsed;
};

/**
 * 등급별 색상 가져오기
 * @param grade 등급
 * @returns 색상 코드
 */
export const getGradeColor = (grade: DeviceGrade): string => {
  return GRADE_CONSTANTS.COLORS[grade];
};

/**
 * 등급별 라벨 가져오기
 * @param grade 등급
 * @returns 라벨 문자열
 */
export const getGradeLabel = (grade: DeviceGrade): string => {
  return GRADE_CONSTANTS.LABELS[grade];
};

/**
 * 등급별 설명 가져오기
 * @param grade 등급
 * @returns 설명 문자열
 */
export const getGradeDescription = (grade: DeviceGrade): string => {
  return GRADE_CONSTANTS.DESCRIPTIONS[grade];
};

/**
 * 등급별 체크리스트 가져오기
 * @param grade 등급
 * @returns 체크리스트 배열
 */
export const getGradeChecklist = (grade: DeviceGrade): string[] => {
  return [...GRADE_CONSTANTS.CHECKLISTS[grade]];
};

/**
 * 가격 차이 계산 (등급 간 가격 차이)
 * @param gradePrices 등급별 가격 정보
 * @param fromGrade 시작 등급
 * @param toGrade 대상 등급
 * @returns 가격 차이 (양수: 상승, 음수: 하락)
 */
export const calculatePriceDifference = (
  gradePrices: GradePriceInfo,
  fromGrade: DeviceGrade,
  toGrade: DeviceGrade
): number | null => {
  const fromPrice = gradePrices[fromGrade];
  const toPrice = gradePrices[toGrade];
  
  if (fromPrice === null || toPrice === null) {
    return null;
  }
  
  return toPrice - fromPrice;
};

/**
 * 가격 트렌드 분석 (등급별 가격 변화율)
 * @param gradePrices 등급별 가격 정보
 * @returns 트렌드 정보
 */
export const analyzePriceTrend = (gradePrices: GradePriceInfo) => {
  const grades: DeviceGrade[] = ['A', 'B', 'C', 'D', 'E'];
  const prices = grades.map(grade => gradePrices[grade]).filter(price => price !== null) as number[];
  
  if (prices.length < 2) {
    return { trend: 'insufficient_data', changeRate: 0 };
  }
  
  const totalChange = prices[prices.length - 1] - prices[0];
  const changeRate = (totalChange / prices[0]) * 100;
  
  let trend: 'increasing' | 'decreasing' | 'stable' = 'stable';
  if (changeRate > 5) trend = 'increasing';
  else if (changeRate < -5) trend = 'decreasing';
  
  return { trend, changeRate: Math.round(changeRate * 100) / 100 };
};

/**
 * 가격 애니메이션을 위한 숫자 보간
 * @param start 시작 값
 * @param end 끝 값
 * @param progress 진행률 (0-1)
 * @returns 보간된 값
 */
export const interpolatePrice = (start: number, end: number, progress: number): number => {
  return Math.round(start + (end - start) * progress);
};

/**
 * 가격 유효성 검사 (실시간 입력 검증)
 * @param input 입력값
 * @returns 검증 결과
 */
export const validatePriceInput = (input: string): PriceValidationResult => {
  if (!input || input.trim() === '') {
    return { isValid: true }; // 빈 값은 허용
  }
  
  const parsed = parsePrice(input);
  if (parsed === null) {
    return {
      isValid: false,
      error: '올바른 가격을 입력해주세요.',
    };
  }
  
  return validatePriceRange(parsed);
};

/**
 * 등급별 가격 요약 정보 생성
 * @param gradePrices 등급별 가격 정보
 * @returns 요약 정보
 */
export const generatePriceSummary = (gradePrices: GradePriceInfo) => {
  const grades: DeviceGrade[] = ['A', 'B', 'C', 'D', 'E'];
  const validPrices = grades
    .map(grade => ({ grade, price: gradePrices[grade] }))
    .filter(item => item.price !== null) as Array<{ grade: DeviceGrade; price: number }>;
  
  if (validPrices.length === 0) {
    return {
      hasPrices: false,
      minPrice: null,
      maxPrice: null,
      priceRange: null,
      averagePrice: null,
    };
  }
  
  const prices = validPrices.map(item => item.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const averagePrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
  
  return {
    hasPrices: true,
    minPrice,
    maxPrice,
    priceRange: maxPrice - minPrice,
    averagePrice: Math.round(averagePrice),
    validGrades: validPrices.map(item => item.grade),
  };
};
